/* TODO: t() */
import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  StyleProp,
  TextStyle,
} from 'react-native';

import ClickAway from './ClickAway';
import { themeColors } from './Constants';
import Hoverable from './Hoverable';
import Icon, { IconName } from './Icon';
import Text from './Text';

type Props = {
  style?: StyleSet;
  textStyle?: StyleProp<TextStyle>;
  children?: React.ReactNode;
  isDisabled?: boolean;
  title?: string;
  icon?: IconName;
  noBorder?: boolean;
  showCaret?: boolean;
  alignPickerRight?: boolean;
  onShow?: () => void;
  testID?: string;
};

export { Props as DropdownButtonProps };

type PickerPlacement = 'BOTTOM' | 'TOP';
const windowHeight = window.innerHeight;

function DropdownButton(props: Props) {
  const [showPicker, setShowPicker] = React.useState(false);
  const [pickerPlacement, setPickerPlacement] = React.useState<PickerPlacement>(
    'BOTTOM',
  );
  const pickerRef = React.useRef<View>(null);
  let {
    style,
    textStyle,
    children,
    isDisabled,
    title,
    icon,
    noBorder = false,
    showCaret = false,
    alignPickerRight = false,
    testID,
  } = props;

  const togglePicker = () => {
    if (!showPicker) {
      setPickerPosition();
      props.onShow?.();
    } else {
      setShowPicker(!showPicker);
    }
  };

  const setPickerPosition = () => {
    pickerRef.current?.measureInWindow((x, yAxis) => {
      if (yAxis > windowHeight / 2) {
        setPickerPlacement('TOP');
      }
      setShowPicker(true);
    });
  };

  return (
    <View>
      <Hoverable
        render={(isHovered, eventHandlers) => (
          <TouchableOpacity
            onPress={togglePicker}
            activeOpacity={0.6}
            disabled={isDisabled || showPicker}
          >
            <View
              testID={testID}
              ref={pickerRef}
              style={[
                styles.root,
                isHovered && styles.hoveredRoot,
                !noBorder && styles.border,
                style,
              ]}
              {...eventHandlers}
            >
              {icon && <Icon name={icon} size="S" />}
              <Text
                numberOfLines={1}
                title={title}
                style={[styles.label, textStyle]}
              >
                {title}
              </Text>
              {showCaret && (
                <Icon
                  name={showPicker ? 'arrowUp' : 'arrowDown'}
                  size="M"
                  style={[styles.iconArrowDown, textStyle]}
                />
              )}
            </View>
          </TouchableOpacity>
        )}
      />
      {showPicker && (
        <ClickAway onClickAway={togglePicker} closeOnClick>
          <View
            collapsable={false}
            style={[
              styles.picker,
              pickerPlacement === 'BOTTOM'
                ? styles.pickerBottom
                : styles.pickerTop,
              alignPickerRight ? { right: 0 } : { left: 0 },
            ]}
          >
            {children}
          </View>
        </ClickAway>
      )}
    </View>
  );
}

let styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingLeft: 12,
    paddingRight: 6,
    backgroundColor: themeColors.white,
    maxWidth: 200,
  },
  border: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 3,
    borderColor: themeColors.borderGrey,
  },
  hoveredRoot: {
    backgroundColor: themeColors.lightGrey,
  },
  picker: {
    position: 'absolute',
    backgroundColor: themeColors.white,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    borderRadius: 4,
    zIndex: 2,
  },
  pickerBottom: {
    marginTop: 4,
    top: 34,
  },
  pickerTop: {
    marginBottom: 4,
    bottom: 34,
  },
  label: {
    flex: 1,
  },
  iconArrowDown: {
    marginLeft: 6,
  },
});

export default DropdownButton;
